import React, { Component } from 'react';
import toast from 'react-hot-toast';
import { api, computeFinancials, fmt } from '../../utils/api';
import SaleGuestDetails from './SaleGuestDetails';
import HotelDetails from './HotelDetails';
import PLBSection from './PLBSection';
import { ChevronLeft, ChevronRight, Send, RefreshCw } from 'lucide-react';

const STEPS = [
  { id: 0, label: 'Sale Details',    short: 'Sale'   },
  { id: 1, label: 'Hotel & Rates',   short: 'Hotel'  },
  { id: 2, label: 'Guest Details',   short: 'Guest'  },
  { id: 3, label: 'PLB',             short: 'PLB'    },
  { id: 4, label: 'Review & Submit', short: 'Submit' },
];

const DEFAULT_FORM = {
  // Sale
  sale_type:    'Hotel',
  booking_date: new Date().toISOString().slice(0, 10),
  booked_by:    '',
  branch:       '',
  conf_no:      '',
  // Hotel
  hotel_name:              '',
  destination:             '',
  check_in:                '',
  check_out:               '',
  no_of_nights:            0,
  total_nights:            0,
  room_type:               '',
  room_type_bookings:      [],
  single_rooms:            0,
  double_rooms:            0,
  extra_bed:               0,
  extra_bed_child:         0,
  extra_bed_adult:         0,
  extra_bed_child_gst:     0,
  extra_bed_adult_gst:     0,
  hotel_email_to:          '',
  hotel_email_cc:          '',
  send_hotel_email:        true,
  mode:                    '',
  ex_bed_rate_sale:        0,
  ex_bed_rate_child_sale:  0,
  tax_rate_sale:           0,
  room_tac_passed:         0,
  ex_bed_tac_passed:       0,
  extras_amount_sale:      0,
  extras_details_sale:     '',
  purchase_from:           '',
  purchase_from_vendor_id: null,
  billing_state_purchase:  '',
  ex_bed_rate_purchase:    0,
  ex_bed_rate_child_purchase: 0,
  tax_rate_purchase:       0,
  tac_on_room:             0,
  tac_on_ex_bed:           0,
  extras_amount_purchase:  0,
  extras_details_purchase: '',
  negative_reason:         '',
  // Guest
  guest_name:          '',
  guest_phone:         '',
  sold_to:             '',
  client_email:        '',
  billing_state_sale:  '',
  // PLB — only store plb_nights, nothing else
  plb_nights: '',
};

// ── helpers ───────────────────────────────────────────────────────────────────
const stateMismatchDetect = (formData) =>
  formData.mode === 'Full Input' &&
  formData.billing_state_sale &&
  formData.billing_state_purchase &&
  formData.billing_state_sale !== formData.billing_state_purchase;

// ── Build a specific, actionable toast message from a validation error object.
// Priority runs from most specific (a single room's missing field) to most
// general, so the toast tells the user exactly what to fix first rather than
// a generic "please fix the errors" message.
function buildErrorToast(errs) {
  if (errs.room_type_errors && errs.room_type_errors.length > 0) {
    const first = errs.room_type_errors[0];
    if (first.occupancy_type) return `${first.room_name}: Enter Occupancy Type`;
    if (first.meal_type)      return `${first.room_name}: Enter Meal Type`;
    if (first.purchase_price) return `${first.room_name}: Enter Purchase Rate`;
    if (first.sale_price)     return `${first.room_name}: Enter Sale Rate`;
    if (first.no_of_persons)  return `${first.room_name}: Enter No. of Persons`;
  }
  if (errs.negative_reason) return 'Enter response for negative GP';
  if (errs.room_type_bookings) {
    return typeof errs.room_type_bookings === 'string'
      ? errs.room_type_bookings
      : 'Please add at least one room type';
  }
  if (errs.hotel_name)             return 'Please select a Hotel';
  if (errs.destination)            return 'Enter Destination';
  if (errs.check_in)               return errs.check_in;
  if (errs.check_out)              return errs.check_out;
  if (errs.mode)                   return 'Please select a Rate Mode';
  if (errs.vendor_agent_id)        return errs.vendor_agent_id;
  if (errs.purchase_from)          return 'Enter Purchase From';
  if (errs.billing_state_purchase) return 'Select Purchase Billing State';
  if (errs.sale_type)              return 'Select Sale Type';
  if (errs.booking_date)           return 'Enter Booking Date';
  if (errs.booked_by)              return 'Enter Booked By';
  if (errs.sold_to)                return 'Enter Sold To';
  if (errs.client_email)           return errs.client_email;
  if (errs.guest_phone)            return errs.guest_phone;
  if (errs.billing_state_sale)     return errs.billing_state_sale;
  return 'Please fix the errors before continuing.';
}

// ── Compute correct financials from room_type_bookings ────────────────────────
function computeLiveFinancials(formData) {
  const rtBookings = formData.room_type_bookings || [];
  const nights     = parseInt(formData.no_of_nights) || 0;
  const mode       = formData.mode || 'Full Input';
  const isSC       = mode === 'Service Charge';
  const extras     = parseFloat(formData.extras_amount_sale) || 0;
  const extrasPur  = parseFloat(formData.extras_amount_purchase) || 0;

  if (rtBookings.length === 0) return computeFinancials(formData);

  let purBase = 0, saleBase = 0, saleBaseNoExtras = 0, aggregatedGSTSale = 0, aggregatedGSTPur = 0, tacTotal = 0, saleTacTotal = 0;

  for (const b of rtBookings) {
    const qty       = parseInt(b.count) || 0;
    const tacRoom   = parseFloat(b.tac_on_room)   || 0;
    const tacExBed  = parseFloat(b.tac_on_ex_bed)  || 0;
    const tacRoomSale  = parseFloat(b.room_tac_passed)   || 0;
    const tacExBedSale = parseFloat(b.ex_bed_tac_passed) || 0;
    const sale      = qty * nights * (parseFloat(b.sale_price) || 0);
    const pur       = qty * nights * (parseFloat(b.purchase_price) || 0);  // gross — TAC not included
    const ebcSale   = (parseInt(b.extra_bed_child)||0) * nights * (parseFloat(b.ex_bed_rate_child_sale)||0);
    const ebaSale   = (parseInt(b.extra_bed_adult)||0) * nights * (parseFloat(b.ex_bed_rate_sale)||0);
    const ebcPur    = (parseInt(b.extra_bed_child)||0) * nights * (parseFloat(b.ex_bed_rate_child_purchase)||0);
    const ebaPur    = (parseInt(b.extra_bed_adult)||0) * nights * (parseFloat(b.ex_bed_rate_purchase)||0);  // gross
    const extSale   = parseFloat(b.extras_amount_sale)     || 0;
    const extPur    = parseFloat(b.extras_amount_purchase) || 0;

    saleBase         += sale + ebcSale + ebaSale + extSale;
    saleBaseNoExtras += sale + ebcSale + ebaSale;
    purBase  += pur  + ebcPur  + ebaPur  + extPur;
    tacTotal += nights * (tacRoom + tacExBed);  // per-night rate — multiplied by nights, not room count
    saleTacTotal += nights * (tacRoomSale + tacExBedSale);

    aggregatedGSTSale += (sale + ebcSale + ebaSale) * ((parseFloat(b.gst_rate) || 0) / 100);
    aggregatedGSTPur  += (pur  + ebcPur  + ebaPur)  * ((parseFloat(b.purchase_gst_rate) || 0) / 100);
  }

  const r2 = (n) => Math.round(n * 100) / 100;
  purBase  = r2(purBase);
  saleBase = r2(saleBase);
  saleBaseNoExtras = r2(saleBaseNoExtras);

  const gstSale = r2(aggregatedGSTSale);
  const gstPur  = r2(aggregatedGSTPur);

  // Booking-level extras (top-level formData — separate from per-room extras already in saleBase/purBase)
  const totalSale      = r2(saleBase + gstSale + extras);
  const totalPurchase  = r2(Math.max(0, purBase + gstPur - tacTotal) + extrasPur);  // TAC: flat, after GST
  const serviceCharge  = r2(saleBase - purBase);

  // ── Gross Profit ─────────────────────────────────────────────────────────────
  // Service Charge: GST-inclusive Total Sale − Total Purchase (Total Purchase
  // already nets TAC on the purchase side; TAC on the sale side is ignored).
  // Full Input: two rules, in priority order:
  // 1. Total Purchase < ₹7,500 → Basic Sale − Total Purchase.
  // 2. Otherwise → (Basic Sale − TAC on Sale) − (Basic Purchase − TAC on
  //    Purchase). GST is ignored entirely on both sides here.
  const grossProfitInclGST = r2(totalSale - totalPurchase);
  let grossProfit;
  if (mode === 'Service Charge') {
    grossProfit = grossProfitInclGST;
  } else if (totalPurchase < 7500) {
    grossProfit = r2(saleBaseNoExtras - totalPurchase);
  } else {
    const netSale     = saleBaseNoExtras - saleTacTotal;
    const netPurchase = purBase          - tacTotal;
    grossProfit = r2(netSale - netPurchase);
  }

  return {
    sub_total_sale:             saleBase,
    sub_total_purchase:         purBase,
    tax_amount_sale:            gstSale,
    tax_amount_purchase:        gstPur,
    total_sale:                 totalSale,
    total_purchase:             totalPurchase,
    gross_profit:               grossProfit,
    gross_profit_incl_gst:      grossProfitInclGST,
    service_charge:             serviceCharge,
    is_negative_service_charge: isSC && serviceCharge < 0,
    plb_amount:                 0,
    total_tac:                  tacTotal,
  };
}

// ── Review Panel ─────────────────────────────────────────────────────────────
class ReviewPanel extends Component {
  render() {
    const { formData, fin, stateMismatch } = this.props;
    const fd = (d) => d
      ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      : '—';
    const isSC   = formData.mode === 'Service Charge';
    const nights = parseInt(formData.no_of_nights) || 0;

    const rtBookings = formData.room_type_bookings || [];
    let roomsSummary;
    if (rtBookings.length > 0) {
      roomsSummary = rtBookings.map(b => `${b.room_name} ×${b.count}`).join(', ');
      const ebChild = parseInt(formData.extra_bed_child) || 0;
      const ebAdult = parseInt(formData.extra_bed_adult) || 0;
      if (ebChild + ebAdult > 0)
        roomsSummary += ` | Extra: ${ebChild} child + ${ebAdult} adult`;
    } else {
      roomsSummary = `Single: ${formData.single_rooms||0}, Double: ${formData.double_rooms||0}`;
    }

    // Sum per-room extras
    const totalExtSale = rtBookings.reduce((s, b) => s + (parseFloat(b.extras_amount_sale) || 0), 0)
                         + (parseFloat(formData.extras_amount_sale) || 0);
    const totalExtPur  = rtBookings.reduce((s, b) => s + (parseFloat(b.extras_amount_purchase) || 0), 0)
                         + (parseFloat(formData.extras_amount_purchase) || 0);

    // Sum TAC from room_type_bookings — per-night rate, multiplied by nights
    const totalTAC = rtBookings.reduce((s, b) => {
      const tacRoom = parseFloat(b.tac_on_room)  || 0;
      const tacEB   = parseFloat(b.tac_on_ex_bed) || 0;
      return s + nights * (tacRoom + tacEB);
    }, 0);

    // Gross Profit is ex-GST with TAC subtracted once (flat) — matches the
    // live footer badge and the Full Input mode formula.
    const displayGP = fin.gross_profit;

    const rows = [
      ['Sale Type',      formData.sale_type],
      ['Booking Date',   fd(formData.booking_date)],
      ['Guest',          formData.guest_name],
      ['Hotel',          formData.hotel_name],
      ['Destination',    formData.destination],
      ['Check In → Out', `${fd(formData.check_in)} → ${fd(formData.check_out)} (${formData.no_of_nights || 0} nights)`],
      ['Rooms',          roomsSummary],
      ['Sold To',        formData.sold_to],
      ['Purchase From',  formData.purchase_from],
      ['Mode',           formData.mode],
      // ── Sale ──
      ['Sub Total (Sale)',      `₹${fmt(fin.sub_total_sale)}`],
      ['GST on ' + (isSC ? 'SC' : 'Sale'), `₹${fmt(fin.tax_amount_sale)}`],
      ['Sale Extras',          `₹${fmt(totalExtSale)}`],
      ['Total Sale',           `₹${fmt(fin.total_sale)}`],
      // ── Purchase ──
      ['Sub Total (Purchase)', `₹${fmt(fin.sub_total_purchase)}`],
      ['GST on Purchase',      `₹${fmt(fin.tax_amount_purchase || 0)}`],
      ...(totalTAC > 0 ? [['TAC (Commission)',  `-₹${fmt(totalTAC)}`]] : []),
      ['Purchase Extras',      `₹${fmt(totalExtPur)}`],
      ['Total Purchase',       `₹${fmt(fin.total_purchase)}`],
      ...(isSC ? [['Service Charge', `₹${fmt(fin.service_charge)}`]] : []),
      ...(formData.plb_nights ? [['PLB Night', `🌙 ${formData.plb_nights} night${formData.plb_nights !== 1 ? 's' : ''}`]] : []),
      ['Gross Profit',         `₹${fmt(displayGP)}`],
    ];

    return (
      <div className="space-y-4">
        {stateMismatch && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 text-red-700 text-sm">
            ❌ Cannot submit — state mismatch in Full Input mode.
            Sale state <strong>{formData.billing_state_sale}</strong> ≠ Purchase state <strong>{formData.billing_state_purchase}</strong>.
          </div>
        )}
        {fin.is_negative_service_charge && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-amber-700 text-sm">
            ⚠️ Negative Service Charge (₹{fmt(fin.service_charge)}). Booking will need Admin approval.
          </div>
        )}
        {!fin.is_negative_service_charge && displayGP < 0 && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-3 text-red-700 text-sm">
            ⚠️ Negative Gross Profit (₹{fmt(displayGP)}). Admin approval required.
          </div>
        )}

        {/* Room type pricing breakdown */}
        {rtBookings.length > 0 && (
          <div className="rounded-xl border border-indigo-100 overflow-hidden">
            <div className="bg-indigo-50 px-4 py-2 text-xs font-bold text-indigo-700 uppercase tracking-wider">
              Room Type Breakdown {isSC && <span className="ml-2 normal-case text-purple-600">(Service Charge — GST on SC only)</span>}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-indigo-600 text-white">
                  {['Room','Qty','Nights','Pur/rm','Pur GST%','Sale/rm','Sale GST%', 'Extras Sale','Extras Pur','Sale Total'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rtBookings.map((b, i) => {
                  const qty      = parseInt(b.count) || 0;
                  const units    = qty * nights;
                  const saleBase = units * (parseFloat(b.sale_price) || 0);
                  const purBase  = units * (parseFloat(b.purchase_price) || 0);
                  const gstRate  = parseFloat(b.gst_rate) || 0;
                  const purGstRate = parseFloat(b.purchase_gst_rate) || 0;
                  const extSale  = parseFloat(b.extras_amount_sale) || 0;
                  const extPur   = parseFloat(b.extras_amount_purchase) || 0;
                  // Extra beds
                  const ebChild = parseInt(b.extra_bed_child) || 0;
                  const ebAdult = parseInt(b.extra_bed_adult) || 0;
                  let gstAmt, saleTotal;
                  if (isSC) {
                    const sc = Math.max(0, saleBase - purBase);
                    gstAmt    = Math.round(sc * gstRate / 100 * 100) / 100;
                    saleTotal = Math.round((saleBase + gstAmt + extSale) * 100) / 100;
                  } else {
                    gstAmt    = Math.round(saleBase * gstRate / 100 * 100) / 100;
                    saleTotal = Math.round((saleBase + gstAmt + extSale) * 100) / 100;
                  }
                  return (
                    <React.Fragment key={b.room_type_id}>
                      <tr className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {b.room_name}
                          {b.meal_type && <span className="ml-1 text-xs text-purple-600">[{b.meal_type}]</span>}
                          {b.occupancy_type && <span className="ml-1 text-xs text-slate-400">({b.occupancy_type === 'Single Occupancy' ? 'Single' : 'Double'})</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{qty}</td>
                        <td className="px-3 py-2 text-slate-600">{nights}</td>
                        <td className="px-3 py-2 text-slate-600">₹{fmt(b.purchase_price || 0)}</td>
                        <td className="px-3 py-2 text-slate-600">{purGstRate}%</td>
                        <td className="px-3 py-2 text-slate-600">₹{fmt(b.sale_price || 0)}</td>
                        <td className="px-3 py-2 text-slate-600">{gstRate}%</td>
                        <td className="px-3 py-2 text-slate-600">{extSale > 0 ? `₹${fmt(extSale)}` : '—'}</td>
                        <td className="px-3 py-2 text-slate-600">{extPur > 0 ? `₹${fmt(extPur)}` : '—'}</td>
                        <td className="px-3 py-2 font-bold text-indigo-700">₹{fmt(saleTotal)}</td>
                      </tr>
                      {/* Extra bed sub-row */}
                      {(ebChild + ebAdult) > 0 && (
                        <tr className="bg-amber-50">
                          <td className="px-3 py-1 pl-6 text-xs text-amber-700" colSpan={3}>
                            🛏️ Extra Bed:
                            {ebChild > 0 && ` Child×${ebChild} (₹${fmt(b.ex_bed_rate_child_purchase||0)}/₹${fmt(b.ex_bed_rate_child_sale||0)})`}
                            {ebAdult > 0 && ` Adult×${ebAdult} (₹${fmt(b.ex_bed_rate_purchase||0)}/₹${fmt(b.ex_bed_rate_sale||0)})`}
                          </td>
                          <td className="px-3 py-1 text-xs text-amber-700" colSpan={2}>
                            GST: {ebChild > 0 ? `Child ${b.extra_bed_child_gst||0}%` : ''}{ebAdult > 0 ? ` Adult ${b.extra_bed_adult_gst||0}%` : ''}
                          </td>
                          <td className="px-3 py-1 text-xs text-amber-700" colSpan={4}>
                            {b.no_of_adults > 0 && `Adults: ${b.no_of_adults}`}
                            {b.no_of_children > 0 && ` · Children: ${b.no_of_children}`}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-indigo-700 text-white">
                  <td colSpan={7} className="px-3 py-2 text-xs font-bold uppercase tracking-wide">
                    Totals {isSC && <span className="ml-2 text-indigo-200 normal-case font-normal">SC: ₹{fmt(fin.service_charge)} → GST: ₹{fmt(fin.tax_amount_sale)}</span>}
                  </td>
                  <td colSpan={2} className="px-3 py-2 text-xs font-bold">GST: ₹{fmt(fin.tax_amount_sale)}</td>
                  <td className="px-3 py-2 font-bold text-lg">₹{fmt(fin.total_sale)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Summary table */}
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {rows.map(([k, v], i) => (
                <tr key={k} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                  <td className="px-4 py-2.5 text-slate-500 font-medium w-40">{k}</td>
                  <td className={`px-4 py-2.5 font-semibold ${
                    k === 'Total Sale'            ? 'text-indigo-700 text-base' :
                    k === 'Total Purchase'        ? 'text-slate-700 text-base' :
                    k === 'Gross Profit'          ? (displayGP >= 0 ? 'text-emerald-700' : 'text-red-700') :
                    k === 'Sale Extras'           ? 'text-orange-600' :
                    k === 'Purchase Extras'       ? 'text-orange-600' :
                    k === 'TAC (Commission)'      ? 'text-emerald-700' :
                    k === 'GST on Purchase'       ? 'text-amber-700' :
                    k === 'Service Charge'        ? 'text-purple-700' :
                    k === 'PLB Night'             ? 'text-blue-700' :
                    'text-slate-800'
                  }`}>{v || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

// ── Success View ─────────────────────────────────────────────────────────────
class SuccessView extends Component {
  render() {
    const { booking, fin, onReset } = this.props;
    return (
      <div className="text-center py-12 px-6">
        <div className="text-5xl mb-4">✅</div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Booking Created!</h2>
        <p className="text-slate-500 mb-1">ID #{booking.id} — {booking.guest_name}</p>
        <p className={`text-lg font-semibold mb-1 ${booking.status === 'Pending Approval' ? 'text-amber-600' : 'text-emerald-600'}`}>
          Status: {booking.status}
        </p>
        {booking.status === 'Pending Approval' && (
          <p className="text-sm text-amber-600 mb-4 max-w-sm mx-auto">
            {booking.is_negative_sc
              ? 'Negative charge detected. Admin must approve before invoice can be sent.'
              : 'Booking pending Admin approval before invoice can be sent.'}
          </p>
        )}
        <div className="flex gap-4 justify-center mt-4 text-sm font-medium flex-wrap">
          <div className="bg-emerald-50 rounded-lg px-4 py-2 text-emerald-700">Sale: ₹{fmt(booking.total_sale)}</div>
          <div className="bg-blue-50 rounded-lg px-4 py-2 text-blue-700">Purchase: ₹{fmt(booking.total_purchase)}</div>
          <div className={`rounded-lg px-4 py-2 ${booking.gross_profit >= 0 ? 'bg-slate-100 text-slate-700' : 'bg-red-50 text-red-700'}`}>
            GP: ₹{fmt(booking.gross_profit)}
          </div>
        </div>
        <button onClick={onReset} className="btn btn-primary mt-8">+ New Booking</button>
      </div>
    );
  }
}

// ── Main BookingForm ─────────────────────────────────────────────────────────
class BookingForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step:         0,
      formData:     { ...DEFAULT_FORM },
      errors:       {},
      submitting:   false,
      savedBooking: null,
    };
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleNext        = this.handleNext.bind(this);
    this.handleBack        = this.handleBack.bind(this);
    this.handleSubmit      = this.handleSubmit.bind(this);
    this.handleReset       = this.handleReset.bind(this);
  }

  componentDidMount() {
    // Returning from the "Add Agent" or "Add Hotel" detour: restore the
    // in-progress booking (formData + step) and apply whichever was created.
    // (newlyAddedAgent itself is passed straight through to SaleGuestDetails
    // below — it reuses its own handleAgentSelect to fill email/billing
    // state/branches exactly as a manual selection would, so it's not
    // duplicated here.)
    const { resumeDraft, newlyAddedHotel, onDraftConsumed } = this.props;
    if (resumeDraft || this.props.newlyAddedAgent || newlyAddedHotel) {
      this.setState(prev => {
        let formData = resumeDraft ? resumeDraft.formData : prev.formData;
        const step   = resumeDraft ? resumeDraft.step     : prev.step;
        if (newlyAddedHotel) {
          const destination = [newlyAddedHotel.city, newlyAddedHotel.billing_state].filter(Boolean).join(', ');
          formData = {
            ...formData,
            hotel_name:              newlyAddedHotel.vendor_name || formData.hotel_name,
            destination:             destination || formData.destination,
            purchase_from_vendor_id: newlyAddedHotel.id ?? formData.purchase_from_vendor_id,
            purchase_from:           newlyAddedHotel.vendor_name || formData.purchase_from,
            billing_state_purchase:  newlyAddedHotel.billing_state || formData.billing_state_purchase,
          };
        }
        return { formData, step };
      }, () => { if (onDraftConsumed) onDraftConsumed(); });
    }
  }

  handleFieldChange(name, value) {
    this.setState(prev => ({
      formData: { ...prev.formData, [name]: value },
      errors:   { ...prev.errors,  [name]: undefined },
    }));
  }

  validateStep(step) {
    const { formData } = this.state;
    const errs = {};

    if (step === 0) {
      if (!formData.sale_type)    errs.sale_type    = 'Required';
      if (!formData.booking_date) errs.booking_date = 'Required';
      if (!formData.booked_by)    errs.booked_by    = 'Required';
    }

    if (step === 1) {
      if (!formData.hotel_name)             errs.hotel_name             = 'Please search and select a hotel';
      if (!formData.destination)            errs.destination            = 'Required';
      if (!formData.check_in)               errs.check_in               = 'Check-in date is required';
      if (!formData.check_out)              errs.check_out              = 'Check-out date is required';
      if (!formData.mode)                   errs.mode                   = 'Please select a Rate Mode before adding rooms';
      if (!formData.purchase_from)          errs.purchase_from          = 'Required';
      if (!formData.billing_state_purchase) errs.billing_state_purchase = 'Required';

      // Vendor type: if Vendor selected, agent is mandatory
      if (formData.vendor_type === 'Vendor' && !formData.vendor_agent_id) {
        errs.vendor_agent_id = 'Please select a Vendor Agent — it is required when Vendor type is selected';
      }

      // Must have at least one room type
      const rtBookings = formData.room_type_bookings || [];
      if (rtBookings.length === 0) {
        errs.room_type_bookings = 'Please add at least one room type using the + button';
      } else {
        // Per-room validation
        const roomErrors = [];
        for (const b of rtBookings) {
          const rErr = {};
          if (!b.occupancy_type)                    rErr.occupancy_type  = 'Select Occupancy Type (Single / Double)';
          if (!b.no_of_persons || parseInt(b.no_of_persons) < 1) rErr.no_of_persons = 'Enter No. of Persons';
          if (!b.meal_type)                         rErr.meal_type       = 'Select a Meal Plan (EP / CP / MAP / AP)';
          if (!(parseFloat(b.purchase_price) > 0))  rErr.purchase_price  = 'Enter Purchase Rate';
          if (!(parseFloat(b.sale_price) > 0))      rErr.sale_price      = 'Enter Sale Rate';
          if (Object.keys(rErr).length > 0) {
            roomErrors.push({ room_name: b.room_name, ...rErr });
          }
        }
        if (roomErrors.length > 0) {
          // Store as a structured error key
          errs.room_type_bookings = roomErrors.map(r => {
            const missing = [];
            if (r.occupancy_type) missing.push('Occupancy Type');
            if (r.no_of_persons)  missing.push('No. of Persons');
            if (r.meal_type)      missing.push('Meal Plan');
            if (r.purchase_price) missing.push('Purchase Rate');
            if (r.sale_price)     missing.push('Sale Rate');
            return `${r.room_name}: Missing — ${missing.join(', ')}`;
          }).join(' | ');
          // Also store per-room errors for HotelDetails inline display
          errs.room_type_errors = roomErrors;
        }
      }

      const fin = computeLiveFinancials(formData);
      if (fin.gross_profit < 0 && fin.sub_total_sale > 0 && !formData.negative_reason?.trim()) {
        errs.negative_reason = 'Reason is required when Gross Profit is negative';
      }
    }

    if (step === 2) {
      if (!formData.guest_name?.trim()) errs.guest_name = 'Guest name is required';
      if (!formData.sold_to) errs.sold_to = 'Required';
      if (!formData.client_email?.trim()) {
        errs.client_email = 'Client email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email.trim())) {
        errs.client_email = 'Enter a valid email address';
      }
      if (formData.guest_phone) {
        const digits = formData.guest_phone.replace(/[^0-9]/g, '');
        if (digits.length < 7 || digits.length > 13)
          errs.guest_phone = 'Enter a valid phone number (7–13 digits)';
      }
      if (!formData.billing_state_sale) errs.billing_state_sale = 'Required';
      if (stateMismatchDetect(formData)) {
        errs.billing_state_sale =
          `State mismatch: Full Input requires Sale state (${formData.billing_state_sale}) ` +
          `to match Hotel/Purchase state (${formData.billing_state_purchase}).`;
      }
    }

    // Step 3 — PLB: no validation needed, plb_nights is optional

    return errs;
  }

  handleNext() {
    const { step } = this.state;
    const errs = this.validateStep(step);
    if (Object.keys(errs).length > 0) {
      this.setState({ errors: errs });
      toast.error(buildErrorToast(errs));
      return;
    }
    this.setState({ step: Math.min(step + 1, STEPS.length - 1), errors: {} });
  }

  handleBack() {
    this.setState(prev => ({ step: Math.max(prev.step - 1, 0), errors: {} }));
  }

  async handleSubmit() {
    const { formData } = this.state;
    if (stateMismatchDetect(formData)) {
      toast.error('Fix state mismatch before submitting.');
      this.setState({ step: 1 });
      return;
    }
    this.setState({ submitting: true });
    try {
      const res = await api.post('/bookings', formData);
      this.setState({ savedBooking: res.data.data });
      toast.success(res.data.message || 'Booking created!');
      if (this.props.onSuccess) this.props.onSuccess(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to create booking');
    } finally {
      this.setState({ submitting: false });
    }
  }

  handleReset() {
    this.setState({ step: 0, formData: { ...DEFAULT_FORM }, errors: {}, submitting: false, savedBooking: null });
  }

  render() {
    const { step, formData, errors, submitting, savedBooking } = this.state;
    const fin               = computeLiveFinancials(formData);
    const liveGP             = fin.gross_profit;
    const stateMismatch = stateMismatchDetect(formData);

    if (savedBooking) {
      return (
        <div className="booking-form-wrapper">
          <SuccessView booking={savedBooking} fin={fin} onReset={this.handleReset} />
        </div>
      );
    }

    return (
      <div className="booking-form-wrapper">

        {/* Step Bar */}
        <div className="step-bar">
          {STEPS.map((s, i) => (
            <button key={s.id} type="button"
              onClick={() => {
                if (i < step) {
                  this.setState({ step: i, errors: {} });
                } else if (i > step) {
                  let allValid = true;
                  for (let j = step; j < i; j++) {
                    const errs = this.validateStep(j);
                    if (Object.keys(errs).length > 0) { allValid = false; this.setState({ errors: errs }); break; }
                  }
                  if (allValid) this.setState({ step: i, errors: {} });
                }
              }}
              className={`step-item ${i === step ? 'active' : i < step ? 'done' : ''}`}
              style={{ cursor: i > step ? 'not-allowed' : 'pointer', opacity: i > step + 1 ? 0.5 : 1 }}
            >
              <span className="step-num">{i < step ? '✓' : i + 1}</span>
              <span className="step-label">{s.short}</span>
            </button>
          ))}
        </div>

        {/* Form Body */}
        <div className="form-body">
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#E8EEFF', marginBottom: 24, letterSpacing: '-0.01em' }}>
            {STEPS[step].label}
          </h2>

          {step === 0 && <SaleGuestDetails section="sale" formData={formData} errors={errors} onChange={this.handleFieldChange} user={this.props.user} newlyAddedAgent={this.props.newlyAddedAgent} onAddAgent={() => this.props.onRequestAddAgent && this.props.onRequestAddAgent(this.state.formData, this.state.step)} />}

          {step === 1 && <HotelDetails formData={formData} errors={errors} onChange={this.handleFieldChange} user={this.props.user} onAddHotel={() => this.props.onRequestAddHotel && this.props.onRequestAddHotel(this.state.formData, this.state.step)} />}

          {step === 2 && (
            <>
              {stateMismatch && (
                <div style={{ marginBottom: 16, padding: '12px 16px', background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 12, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: '#991B1B', marginBottom: 4 }}>❌ State Mismatch — Full Input Mode</div>
                  <div style={{ color: '#B91C1C' }}>
                    Sale state <strong>{formData.billing_state_sale}</strong> ≠ Purchase state <strong>{formData.billing_state_purchase}</strong>.
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: '#6B7280' }}>
                    Fix: Change the agent's billing state or switch to <strong>Service Charge</strong> mode.
                  </div>
                </div>
              )}
              <SaleGuestDetails section="guest" formData={formData} errors={errors} onChange={this.handleFieldChange} user={this.props.user} newlyAddedAgent={this.props.newlyAddedAgent} onAddAgent={() => this.props.onRequestAddAgent && this.props.onRequestAddAgent(this.state.formData, this.state.step)} />
            </>
          )}

          {step === 3 && <PLBSection formData={formData} onChange={this.handleFieldChange} />}

          {step === 4 && <ReviewPanel formData={formData} fin={fin} stateMismatch={stateMismatch} />}
        </div>

        {/* Footer */}
        <div className="form-footer">
          <button type="button" onClick={this.handleBack} disabled={step === 0} className="btn btn-ghost">
            <ChevronLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2">
            <span style={{ fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:20, background: liveGP >= 0 ? '#ECFDF5' : '#FEF2F2', color: liveGP >= 0 ? '#065F46' : '#991B1B', border: `1px solid ${liveGP >= 0 ? '#A7F3D0' : '#FECACA'}`, whiteSpace:'nowrap' }}>
              GP: ₹{new Intl.NumberFormat('en-IN').format(liveGP)}
            </span>
            {stateMismatch && (
              <span style={{ fontSize: 11, background: '#FEE2E2', color: '#991B1B', borderRadius: 20, padding: '3px 10px', fontWeight: 700 }}>
                ⚠️ State Mismatch
              </span>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={this.handleNext} className="btn btn-primary">
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button type="button" onClick={this.handleSubmit} disabled={submitting || stateMismatch} className="btn btn-success">
                {submitting ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
                {submitting ? 'Saving...' : 'Submit Booking'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default BookingForm;